import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { PlayerProfile, GameRecord } from '@/types';
import { 
  createProfile, 
  getProfile, 
  getAllProfiles, 
  updateProfile, 
  deleteProfile,
  checkIn,
  claimRelief,
  saveGameRecord,
  getRecentRecords,
} from '@/db';

interface PlayerState {
  // 当前选中的档案
  currentProfile: PlayerProfile | null;
  // 所有档案
  profiles: PlayerProfile[];
  // 最近游戏记录
  recentRecords: GameRecord[];
  // 加载状态
  isLoading: boolean;
  
  // 操作
  loadProfiles: () => Promise<void>;
  selectProfile: (profileId: string) => Promise<void>;
  createNewProfile: (name: string, avatar: string) => Promise<PlayerProfile>;
  updateCurrentProfile: (updates: Partial<PlayerProfile>) => Promise<void>;
  deleteCurrentProfile: () => Promise<void>;
  
  // 游戏相关
  addChips: (amount: number) => Promise<void>;
  deductChips: (amount: number) => Promise<boolean>;
  recordGame: (record: Omit<GameRecord, 'id' | 'profileId' | 'timestamp'>) => Promise<void>;
  loadRecentRecords: (limit?: number) => Promise<void>;
  
  // 每日功能
  performCheckIn: () => Promise<{ success: boolean; reward: number }>;
  claimBankruptcyRelief: () => Promise<{ success: boolean; amount: number; remaining: number }>;
  
  // 统计更新
  updateStats: (stats: Partial<PlayerProfile['stats']>) => Promise<void>;
}

export const usePlayerStore = create<PlayerState>()(
  persist(
    (set, get) => ({
      currentProfile: null,
      profiles: [],
      recentRecords: [],
      isLoading: false,

      loadProfiles: async () => {
        set({ isLoading: true });
        try {
          const profiles = await getAllProfiles();
          set({ profiles });
        } finally {
          set({ isLoading: false });
        }
      },

      selectProfile: async (profileId: string) => {
        set({ isLoading: true });
        try {
          const profile = await getProfile(profileId);
          if (profile) {
            set({ currentProfile: profile });
            // 加载最近记录
            const records = await getRecentRecords(profileId, 10);
            set({ recentRecords: records });
          }
        } finally {
          set({ isLoading: false });
        }
      },

      createNewProfile: async (name: string, avatar: string) => {
        const profile = await createProfile(name, avatar);
        set(state => ({ 
          profiles: [...state.profiles, profile],
          currentProfile: profile,
        }));
        return profile;
      },

      updateCurrentProfile: async (updates: Partial<PlayerProfile>) => {
        const { currentProfile } = get();
        if (!currentProfile) return;

        await updateProfile(currentProfile.id, updates);
        const updated = await getProfile(currentProfile.id);
        if (updated) {
          set({ 
            currentProfile: updated,
            profiles: get().profiles.map(p => 
              p.id === updated.id ? updated : p
            ),
          });
        }
      },

      deleteCurrentProfile: async () => {
        const { currentProfile } = get();
        if (!currentProfile) return;

        await deleteProfile(currentProfile.id);
        set(state => ({
          currentProfile: null,
          profiles: state.profiles.filter(p => p.id !== currentProfile.id),
          recentRecords: [],
        }));
      },

      addChips: async (amount: number) => {
        const { currentProfile } = get();
        if (!currentProfile) return;

        await updateProfile(currentProfile.id, {
          chips: currentProfile.chips + amount,
        });
        
        const updated = await getProfile(currentProfile.id);
        if (updated) {
          set({ currentProfile: updated });
        }
      },

      deductChips: async (amount: number) => {
        const { currentProfile } = get();
        if (!currentProfile) return false;

        if (currentProfile.chips < amount) {
          return false;
        }

        await updateProfile(currentProfile.id, {
          chips: currentProfile.chips - amount,
        });
        
        const updated = await getProfile(currentProfile.id);
        if (updated) {
          set({ currentProfile: updated });
        }
        return true;
      },

      recordGame: async (record) => {
        const { currentProfile } = get();
        if (!currentProfile) return;

        const fullRecord: GameRecord = {
          ...record,
          id: `record-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          profileId: currentProfile.id,
          timestamp: Date.now(),
        };

        await saveGameRecord(fullRecord);
        
        // 刷新记录和档案
        const records = await getRecentRecords(currentProfile.id, 10);
        const updated = await getProfile(currentProfile.id);
        
        set({ 
          recentRecords: records,
          currentProfile: updated || currentProfile,
        });
      },

      loadRecentRecords: async (limit = 10) => {
        const { currentProfile } = get();
        if (!currentProfile) return;

        const records = await getRecentRecords(currentProfile.id, limit);
        set({ recentRecords: records });
      },

      performCheckIn: async () => {
        const { currentProfile } = get();
        if (!currentProfile) return { success: false, reward: 0 };

        const result = await checkIn(currentProfile.id);
        
        if (result.success) {
          const updated = await getProfile(currentProfile.id);
          if (updated) {
            set({ currentProfile: updated });
          }
        }
        
        return result;
      },

      claimBankruptcyRelief: async () => {
        const { currentProfile } = get();
        if (!currentProfile) return { success: false, amount: 0, remaining: 0 };

        const result = await claimRelief(currentProfile.id);
        
        if (result.success) {
          const updated = await getProfile(currentProfile.id);
          if (updated) {
            set({ currentProfile: updated });
          }
        }
        
        return result;
      },

      updateStats: async (stats) => {
        const { currentProfile } = get();
        if (!currentProfile) return;

        await updateProfile(currentProfile.id, {
          stats: { ...currentProfile.stats, ...stats },
        });
        
        const updated = await getProfile(currentProfile.id);
        if (updated) {
          set({ currentProfile: updated });
        }
      },
    }),
    {
      name: 'poker-player-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ 
        currentProfile: state.currentProfile,
        profiles: state.profiles,
      }),
    }
  )
);
